import { Button, Col, Popconfirm, Row } from "antd";
import { useCallback, useEffect } from "react";
import { notification } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/layout/BreadCrumbs";
import PageLayout from "../../components/layout/PageLayout/PageLayout";
import TableActions from "../../components/tables/TableActions/TableActions";
import Br from "../../components/ui/Br";
import spacing from "../../components/ui/spacingConfig";
import { useFetchProjectQuery } from "../../features/api/apiSlice";
import { useCrudAction } from "../../features/api/crud/hooks/useCrudAction";
import { useCrudProject } from "../../features/api/crud/hooks/useCrudProject";
import CardComments from "../../components/ui/CardComments/CardComments";
import CardDatePlaceProject from "./components/CardDatePlaceProject";
import CardDescriptionProject from "./components/CardDescriptionProject";
import styles from "./PageProject.module.scss";

const PageProject: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const {
    data: projectData,
    isFetching,
    refetch: refetchProject,
    error,
  } = useFetchProjectQuery(Number(id), {
    skip: !id,
  });

  /**
   * Handle 404 and 400's errors
   */
  useEffect(() => {
    const _error = error as { status?: number } | undefined;

    const notFound = _error?.status && _error?.status === 404;
    const basStatus = _error?.status && _error?.status >= 400;

    if (notFound) {
      notification.error({
        message:
          "Aucun projet trouvÃ©. Veuillez vÃ©rifier vos droits avec l'administrateur.",
      });
      navigate("/pdca/projets");
    } else if (basStatus) {
      notification.error({
        message: "Une erreur est survenue lors de la rÃ©cupÃ©ration du projet.",
      });
      navigate("/pdca/projets");
    }
  }, [error, navigate]);

  const {
    onClose,
    isClosing,
    archiveProject,
    isArchiving,
    comments,
    createComment,
    isCreatingComment,
  } = useCrudProject({
    navigate,
    fetchProject: refetchProject,
    projectId: Number(id),
  });

  /**
   * Navigation handlers
   */
  const navigateToEdit = useCallback(() => {
    navigate(`/projects/${id}/edit`);
  }, [id, navigate]);

  const navigateToActionNew = useCallback(() => {
    navigate(`/projects/${id}/actions/new`);
  }, [id, navigate]);

  /**
   * Actions
   */
  const { onDelete: onDeleteAction, isDeleting: isDeletingAction } =
    useCrudAction(
      undefined,
      refetchProject,
      Number(id),
      undefined,
      undefined,
      "project",
    );

  const isPageLoading = isFetching || isClosing || isArchiving;

  return (
    <PageLayout pageContainerClassName={styles.root} isLoading={isPageLoading}>
      <Row justify="space-between" align="middle">
        {!isFetching && (
          <Col>
            <Breadcrumbs
              pageName="project"
              projectId={projectData?.id}
              projectName={projectData?.name}
            />
          </Col>
        )}
        <Col>
          <Row gutter={spacing.m} className={styles.buttonsContainer}>
            <Col>
              <Button type="primary" onClick={navigateToEdit}>
                Modifier
              </Button>
            </Col>
            <Col>
              <Popconfirm
                title="Confirmez-vous la clÃ´ture de ce projet ?"
                onConfirm={() => onClose(projectData?.id || 0)}
                okText="Oui, clÃ´turer"
                cancelText="Annuler"
              >
                <Button
                  type="primary"
                  disabled={projectData?.statut !== "DECLARE" || isClosing}
                >
                  ClÃ´turer
                </Button>
              </Popconfirm>
            </Col>
            <Col>
              <Popconfirm
                title="Confirmez-vous l'archivage de ce projet ?"
                onConfirm={() => archiveProject(projectData?.id || 0)}
                okText="Oui, archiver"
                cancelText="Annuler"
              >
                <Button
                  type="primary"
                  disabled={projectData?.statut === "ARCHIVED" || isArchiving}
                >
                  Archiver
                </Button>
              </Popconfirm>
            </Col>
          </Row>
        </Col>
      </Row>
      <Br height={spacing.sm} />
      {/**
       * Cards Description and DateAndPlace
       */}
      <Row gutter={spacing.m} className={styles.firstRow}>
        <Col span={spacing.s}>
          <CardDescriptionProject
            projectData={projectData}
            className={styles.cardsEventDetail}
          />
        </Col>
        <Col span={spacing.xxs} className={styles.colDate}>
          <CardDatePlaceProject
            projectData={projectData}
            className={styles.cardsEventDetail}
          />
        </Col>
      </Row>
      <Br height={spacing.xl} />
      {/**
       * Plan d'action
       */}
      <Row>
        <Col span={spacing.m} className={styles.colActionPlan}>
          <div className={styles.actionHeader}>
            <h3>Plan d'action</h3>
            <Button type="primary" onClick={navigateToActionNew}>
              Ajouter une nouvelle action
            </Button>
          </div>
          <Br height={spacing.sm} />
          <div className={styles.cardsEventDetail}>
            <TableActions
              actionsData={projectData?.actions}
              fetchData={refetchProject}
              dontFetchOnMount
              isLoading={isFetching || isDeletingAction}
              onDelete={onDeleteAction}
              isColumnForPageEvent={true}
              parentType="project"
            />
          </div>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row>
        <Col span={spacing.m}>
          <CardComments
            comments={comments}
            onAddComment={createComment}
            isSubmitting={isCreatingComment}
            className={styles.cardsEventDetail}
          />
        </Col>
      </Row>
    </PageLayout>
  );
};

export default PageProject;
